/* Preflight: everything that should be fixed or acknowledged before a sheet
   goes to print, in plain words. The same checks run in the browser (live)
   and on the server (when the customer orders). */

import type { BuilderSettings, PricingConfig } from './config.js';
import type { AssetInfo, ProjectDoc } from './model.js';
import { placedCounts, layoutGap, inkGapFor } from './model.js';
import { itemBox, inside, closePairs, type Rect } from './geometry.js';
import { inkGap, shapeFor } from './ink.js';
import { effectiveDpi } from './dpi.js';
import { formatDims } from './units.js';

export type IssueLevel = 'error' | 'warning' | 'info';
export type IssueFix = 'rebuild' | 'acknowledge' | 'select' | 'move_inside' | 'remove_design' | 'remove_sheet' | 'wait' | 'none';

export interface Issue {
  id: string;
  level: IssueLevel;
  code:
    | 'empty' | 'stale' | 'outside' | 'near_edge' | 'overlap' | 'too_close' | 'spacing' | 'low_dpi' | 'blocked_dpi'
    | 'fair_dpi' | 'opaque' | 'missing_asset' | 'processing' | 'failed_asset' | 'unplaced' | 'too_long' | 'sheet_info' | 'empty_sheet';
  title: string;
  detail?: string;
  designId?: string;
  itemIds?: string[];
  sheetIndex?: number;
  /** where on the sheet the artwork is too close or overlaps (mm), for a small marker */
  spots?: Array<{ x: number; y: number }>;
  fix: IssueFix;
}

export interface PreflightResult {
  /** ready = nothing to fix; check = only acknowledgements/warnings; blocked = must fix */
  status: 'ready' | 'check' | 'blocked';
  issues: Issue[];
  errors: number;
  warnings: number;
}

export function preflight(
  doc: ProjectDoc,
  assets: Record<string, AssetInfo | undefined>,
  builder: Pick<BuilderSettings, 'edgeMm' | 'dpiGood' | 'dpiFair' | 'blockBelowDpi' | 'gapMm' | 'minGapMm' | 'allowCustomerGap'> & Partial<Pick<BuilderSettings, 'inkGapMm' | 'spacingCheck'>>,
  pricing?: PricingConfig
): PreflightResult {
  const issues: Issue[] = [];
  const designs = new Map(doc.designs.map(d => [d.id, d]));
  const label = (designId: string) => designs.get(designId)?.name || 'A design';
  const totalItems = doc.sheets.reduce((n, s) => n + s.items.length, 0);
  /* spacing: the shop's minimum can't be undercut (the server checks it, whatever the gangsheet says);
     the customer's own chosen gap is only a warning */
  const { hardGap, gap } = gapRules(doc, builder);

  if (!totalItems) {
    issues.push({ id: 'empty', level: 'error', code: 'empty', title: 'Your gangsheet is empty.', detail: 'Add designs and press Build my gangsheet.', fix: 'rebuild' });
  }
  if (doc.layoutStale && totalItems) {
    issues.push({ id: 'stale', level: 'error', code: 'stale', title: 'Your sizes or quantities changed since this sheet was built.', detail: 'Rebuild it so the sheet matches your list.', fix: 'rebuild' });
  }

  // assets
  for (const d of doc.designs) {
    const a = assets[d.assetId];
    if (!a) {
      issues.push({ id: 'missing:' + d.id, level: 'error', code: 'missing_asset', title: d.name + ': the original file is missing.', detail: 'Upload it again or remove this design.', designId: d.id, fix: 'remove_design' });
      continue;
    }
    if (a.status === 'failed') {
      issues.push({ id: 'failed:' + d.id, level: 'error', code: 'failed_asset', title: d.name + ': we couldn’t read this file.', detail: a.error || 'Try exporting it again as a PNG.', designId: d.id, fix: 'remove_design' });
      continue;
    }
    if (a.status !== 'ready') {
      issues.push({ id: 'processing:' + d.id, level: 'error', code: 'processing', title: d.name + ' is still uploading.', detail: 'It’ll be ready in a moment.', designId: d.id, fix: 'wait' });
      continue;
    }
    if (a.opaque) {
      issues.push({
        id: 'opaque:' + d.id, level: 'warning', code: 'opaque', designId: d.id, fix: 'acknowledge',
        title: d.name + ' has a solid background.',
        detail: 'It has no transparent areas, so the whole rectangle will print, background and all. A PNG with a transparent background prints just the design.'
      });
    }
  }

  // per-item checks (DPI is per copy size, but reported once per design)
  const dpiSeen = new Map<string, number>();
  doc.sheets.forEach((s, si) => {
    // an empty sheet next to others is still charged (0.3d)
    if (!s.items.length && totalItems) issues.push({
      id: 'emptysheet:' + si, level: 'warning', code: 'empty_sheet', sheetIndex: si, fix: 'remove_sheet',
      title: 'Sheet ' + (si + 1) + ' is empty.', detail: 'An empty sheet is still charged. Remove it, or put designs on it.'
    });
    const sheetRect: Rect = { x: 0, y: 0, w: s.widthMm, h: s.lengthMm };
    const e = builder.edgeMm;
    /* a roll's length is billed to the whole mm, so its bottom margin may be up to half a mm short */
    const bottomTol = s.kind === 'fixed' ? 0.05 : 0.5;
    const inSafe = (b: Rect) => b.x >= e - 0.05 && b.y >= e - 0.05 && b.x + b.w <= s.widthMm - e + 0.05 && b.y + b.h <= s.lengthMm - e + bottomTol;
    const outsideIds: string[] = [], nearIds: string[] = [];
    s.items.forEach(it => {
      const b = itemBox(it);
      if (!inside(b, sheetRect, 0.05)) outsideIds.push(it.id);
      else if (!inSafe(b)) nearIds.push(it.id);
      const d = designs.get(it.designId);
      const a = d && assets[d.assetId];
      if (d && a && a.status === 'ready') {
        const r = effectiveDpi(a, d.trim, it.widthMm, it.heightMm, builder);
        const prev = dpiSeen.get(d.id);
        if (prev === undefined || r.dpi < prev) dpiSeen.set(d.id, r.dpi);
      }
    });
    if (outsideIds.length) issues.push({
      id: 'outside:' + si, level: 'error', code: 'outside', sheetIndex: si, itemIds: outsideIds, fix: 'move_inside',
      title: (outsideIds.length === 1 ? '1 design goes' : outsideIds.length + ' designs go') + ' past the edge of sheet ' + (si + 1) + '.',
      detail: 'Anything outside the sheet won’t print.'
    });
    if (nearIds.length) issues.push({
      id: 'near:' + si, level: 'error', code: 'near_edge', sheetIndex: si, itemIds: nearIds, fix: 'move_inside',
      title: (nearIds.length === 1 ? '1 design is' : nearIds.length + ' designs are') + ' closer than ' + builder.edgeMm + ' mm to the edge of sheet ' + (si + 1) + '.',
      detail: 'We need ' + builder.edgeMm + ' mm clear at the edge so nothing gets clipped. Press Move inside.'
    });
    /* spacing: rectangles first (cheap); with the artwork rule, a pair whose rectangles are too close is
       then measured by the ink it actually prints, so transparent corners may overlap */
    const byShape = (builder.spacingCheck ?? 'box') === 'artwork';
    const pairs = closePairs(s.items, gap);
    const overlapIds = new Set<string>(), tightIds = new Set<string>(), closeIds = new Set<string>();
    const spots: Record<'overlap' | 'tight' | 'close', Array<{ x: number; y: number }>> = { overlap: [], tight: [], close: [] };
    const shapeOf = (it: typeof s.items[number]) => { const d = designs.get(it.designId); return shapeFor(d ? assets[d.assetId] : undefined, d ? d.trim : true); };
    for (const p of pairs) {
      const A = s.items[p.a], B = s.items[p.b];
      let kind: 'overlap' | 'tight' | 'close' | null;
      let at: { x: number; y: number } | null = null;
      if (byShape) {
        const g = inkGap(A, shapeOf(A), B, shapeOf(B), gap);
        kind = g.overlap ? 'overlap' : g.distance < hardGap - 0.05 ? 'tight' : g.distance < gap - 0.05 ? 'close' : null;
        at = g.at;
      } else {
        kind = p.overlap ? 'overlap' : p.distance < hardGap - 0.05 ? 'tight' : 'close';
        const ba = itemBox(A), bb = itemBox(B);
        at = { x: (Math.max(ba.x, bb.x) + Math.min(ba.x + ba.w, bb.x + bb.w)) / 2, y: (Math.max(ba.y, bb.y) + Math.min(ba.y + ba.h, bb.y + bb.h)) / 2 };
      }
      if (!kind) continue;
      [A.id, B.id].forEach(id => (kind === 'overlap' ? overlapIds : kind === 'tight' ? tightIds : closeIds).add(id));
      if (at) spots[kind].push(at);
    }
    overlapIds.forEach(id => { tightIds.delete(id); closeIds.delete(id); });
    tightIds.forEach(id => closeIds.delete(id));
    const measure = byShape ? 'printed artwork' : 'designs';
    if (overlapIds.size) issues.push({
      id: 'overlap:' + si, level: 'error', code: 'overlap', sheetIndex: si, itemIds: [...overlapIds], fix: 'select', spots: spots.overlap,
      title: overlapIds.size + ' designs overlap on sheet ' + (si + 1) + '.',
      detail: 'Where the artwork overlaps it prints on top of each other. Move them apart or press Re-nest.'
    });
    if (tightIds.size) issues.push({
      id: 'tight:' + si, level: 'error', code: 'too_close', sheetIndex: si, itemIds: [...tightIds], fix: 'select', spots: spots.tight,
      title: tightIds.size + ' designs are closer than ' + hardGap + ' mm apart on sheet ' + (si + 1) + '.',
      detail: 'We need at least ' + hardGap + ' mm between ' + measure + ' to cut them apart. Move them apart or press Re-nest.'
    });
    if (closeIds.size) issues.push({
      id: 'spacing:' + si, level: 'warning', code: 'spacing', sheetIndex: si, itemIds: [...closeIds], fix: 'select', spots: spots.close,
      title: closeIds.size + ' designs are closer than ' + gap + ' mm apart on sheet ' + (si + 1) + '.',
      detail: 'Your gap setting is ' + gap + ' mm. A bigger gap makes them easier to cut out.'
    });
    if (pricing) {
      const bl = pricing.transfers[doc.transfer].byLength;
      if (s.kind !== 'fixed' && s.lengthMm > bl.maxLengthMm + 0.5) issues.push({
        id: 'long:' + si, level: 'error', code: 'too_long', sheetIndex: si, fix: 'rebuild',
        title: 'Sheet ' + (si + 1) + ' is longer than ' + bl.maxLengthMm / 1000 + ' m.',
        detail: 'Rebuild and we’ll split it over more sheets.'
      });
    }
  });

  for (const [designId, dpi] of dpiSeen) {
    if (!isFinite(dpi)) continue;
    const d = Math.round(dpi);
    if (builder.blockBelowDpi && dpi < builder.blockBelowDpi) {
      issues.push({ id: 'dpi:' + designId, level: 'error', code: 'blocked_dpi', designId, fix: 'select', title: label(designId) + ': ' + d + ' DPI.', detail: 'This is too low to print at this size. Make it smaller or upload a bigger file.' });
    } else if (dpi < builder.dpiFair - 0.5) {
      const acked = doc.acknowledged.includes('dpi:' + designId);
      issues.push({ id: 'dpi:' + designId, level: acked ? 'info' : 'warning', code: 'low_dpi', designId, fix: acked ? 'none' : 'acknowledge', title: label(designId) + ': ' + d + ' DPI.', detail: 'This artwork may appear pixelated at this print size.' });
    } else if (dpi < builder.dpiGood - 0.5) {
      issues.push({ id: 'dpi:' + designId, level: 'info', code: 'fair_dpi', designId, fix: 'none', title: label(designId) + ': ' + d + ' DPI.', detail: 'Should print fine. 300 DPI is best.' });
    }
  }

  // copies that were asked for but aren't on a sheet
  if (totalItems && !doc.layoutStale) {
    const placed = placedCounts(doc);
    for (const d of doc.designs) {
      const n = placed.get(d.id) || 0;
      if (n < d.quantity) issues.push({
        id: 'unplaced:' + d.id, level: 'warning', code: 'unplaced', designId: d.id, fix: 'rebuild',
        title: (d.quantity - n) + ' of ' + d.quantity + ' × ' + d.name + ' ' + (d.quantity - n === 1 ? 'isn’t' : 'aren’t') + ' on a sheet.',
        detail: n === 0 && d.quantity > 0 ? 'It may be too big for the sheet.' : 'Rebuild to fit them in.'
      });
    }
  }

  doc.sheets.forEach((s, si) => issues.push({
    id: 'info:' + si, level: 'info', code: 'sheet_info', sheetIndex: si, fix: 'none',
    title: 'Sheet ' + (si + 1) + ': ' + formatDims(s.widthMm, s.lengthMm, 'cm') + ' · ' + s.items.length + ' transfer' + (s.items.length === 1 ? '' : 's') + '.'
  }));

  // an opaque warning the customer has acknowledged behaves like a low-DPI one
  for (const i of issues) if (i.code === 'opaque' && i.designId && doc.acknowledged.includes('opaque:' + i.designId)) { i.level = 'info'; i.fix = 'none'; }

  const errors = issues.filter(i => i.level === 'error').length;
  const warnings = issues.filter(i => i.level === 'warning').length;
  return { status: errors ? 'blocked' : warnings ? 'check' : 'ready', issues, errors, warnings };
}

/** The gap the layout should keep (a warning below it), and the smallest gap allowed (an error below it,
    also on the server). Artwork shape: the Admin minimum between printed artwork for this transfer type.
    Bounding box: exactly the rule before 0.3c (the shop minimum, or the shop gap when customers can't
    change it), never under the Admin minimum. */
export function gapRules(
  doc: Pick<ProjectDoc, 'settings' | 'transfer'>,
  builder: Pick<BuilderSettings, 'gapMm' | 'minGapMm' | 'allowCustomerGap'> & Partial<Pick<BuilderSettings, 'inkGapMm' | 'spacingCheck'>>
) {
  const ink = inkGapFor(doc, builder);
  const hardGap = (builder.spacingCheck ?? 'box') === 'artwork' ? ink : Math.max(ink, builder.allowCustomerGap ? builder.minGapMm : builder.gapMm);
  const gap = Math.max(layoutGap(doc, builder), hardGap);
  return { hardGap, gap };
}

/** Warnings that need a tick before ordering. */
export function needsAcknowledgement(r: PreflightResult): Issue[] {
  return r.issues.filter(i => i.level === 'warning' && i.fix === 'acknowledge');
}
